import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { Wordmark } from '../brand/Wordmark';
import { NewTabHint } from '../ui/NewTabHint';
import { externalLinks, primaryNav, allRoutes } from '../../data/site';
function desktopLinkClass({
  isActive
}: {
  isActive: boolean;
}) {
  return `relative py-2 font-display text-[15px] font-semibold text-brand-navy transition-colors hover:text-[#083a7c] ${isActive ? 'after:absolute after:-bottom-0.5 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-brand-yellow after:content-[""]' : ''}`;
}
function ResourcesMenu({
  items
}: {
  items: {
    label: string;
    to: string;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const {
    pathname
  } = useLocation();
  const isActive = items.some(item => item.to === pathname);
  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  return <div ref={wrapRef} className="relative" onKeyDown={event => {
    if (event.key === 'Escape') setOpen(false);
  }} data-id="element-293" data-mp="gnueb2dnbchy">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(value => !value)} className={`relative flex items-center gap-1 py-2 font-display text-[15px] font-semibold text-brand-navy hover:text-[#083a7c] ${isActive ? 'after:absolute after:-bottom-0.5 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-brand-yellow after:content-[""]' : ''}`} data-id="element-294" data-mp="3k38j0u3cfoj5">
        Resources
        <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-295" data-mp="1dafsobvad846" />
      </button>
      {open ? <div className="absolute left-0 top-full z-50 mt-2 w-56 rounded-2xl border-2 border-brand-navy bg-white p-2 shadow-paper" data-id="element-296" data-mp="35e3emy2yyr91">
          {items.map(item => <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-display text-[15px] font-semibold text-brand-navy hover:bg-brand-tan" data-id="element-297" data-mp="3edyuhg7gzafe">
              {item.label}
            </Link>)}
        </div> : null}
    </div>;
}
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  return <header className="sticky top-0 z-40 w-full border-b-2 border-brand-navy bg-brand-tan" data-id="element-298" data-mp="eenx6ma77s2l">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8" data-id="element-299" data-mp="1251h1pimqkm3">
        <Link to="/" aria-label="UCF BCM home" className="shrink-0" data-id="element-300" data-mp="166vb3eybkqda">
          <Wordmark variant="blue" className="h-9 sm:h-10" data-id="element-301" data-mp="1640ophvqwvh8" />
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-7 lg:flex" data-id="element-302" data-mp="s6c7ql4su8hg">
          {primaryNav.map(item => item.children ? <ResourcesMenu key={item.label} items={item.children} data-id="element-303" data-mp="3cw9wqia8qlhn" /> : <NavLink key={item.to} to={item.to} end={item.to === '/'} className={desktopLinkClass} data-id="element-304" data-mp="3hvml57grg0mc">
                {item.label}
              </NavLink>)}
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-5 font-display text-[15px] font-semibold text-white transition-colors hover:bg-[#083a7c]" data-id="element-305" data-mp="11zuf2s9qhtw">
            Get Connected
            <NewTabHint data-id="element-306" data-mp="jy820d8r74sd" />
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden" data-id="element-307" data-mp="fwwag26bk9hj">
          <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="hidden min-h-[44px] items-center rounded-full border-2 border-brand-navy bg-brand-navy px-4 font-display text-sm font-semibold text-white sm:inline-flex" data-id="element-308" data-mp="fvbbm0cz24dv">
            Get Connected
            <NewTabHint data-id="element-309" data-mp="2qp7h12v96ct0" />
          </a>
          <button type="button" onClick={() => setMobileOpen(true)} aria-expanded={mobileOpen} aria-controls="mobile-menu" className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy" data-id="element-310" data-mp="2qo5spwp87ttc">
            <MenuIcon aria-hidden="true" className="h-6 w-6" data-id="element-311" data-mp="2qhn5e30n2vny" />
            <span className="sr-only" data-id="element-312" data-mp="2vb84jydyxvoq">Open menu</span>
          </button>
        </div>
      </div>

      {mobileOpen ? <div id="mobile-menu" role="dialog" aria-modal="true" aria-label="Site menu" className="fixed inset-0 z-50 flex flex-col bg-brand-tan lg:hidden" data-id="element-313" data-mp="2zbs1p1yjkzav">
          <div className="flex items-center justify-between border-b-2 border-brand-navy px-5 py-3" data-id="element-314" data-mp="3984vs0y01f65">
            <Link to="/" aria-label="UCF BCM home" data-id="element-315" data-mp="3e6y83q1jv04q">
              <Wordmark variant="blue" className="h-9" data-id="element-316" data-mp="1ex85sqqv0c32" />
            </Link>
            <button type="button" ref={closeRef} onClick={() => setMobileOpen(false)} className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy" data-id="element-317" data-mp="1jurivnbtmitf">
              <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-318" data-mp="16xv24bkzxrs9" />
              <span className="sr-only" data-id="element-319" data-mp="1lucff8cqxghg">Close menu</span>
            </button>
          </div>
          <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-5 py-6" data-id="element-320" data-mp="1gsvhnlpk6a53">
            <ul className="space-y-1" data-id="element-321" data-mp="1uso2j0eelw55">
              {allRoutes.map(route => <li key={route.to} data-id="element-322" data-mp="1puwbe8ll2e20">
                  <NavLink to={route.to} end={route.to === '/'} className={({
              isActive
            }) => `block rounded-2xl px-4 py-3.5 font-display text-xl font-bold text-brand-navy ${isActive ? 'bg-brand-blue' : 'hover:bg-white'}`} data-id="element-323" data-mp="1pttmg67tmloq">
                    {route.label}
                  </NavLink>
                </li>)}
            </ul>
            <a href={externalLinks.getConnectedForm} target="_blank" rel="noopener noreferrer" className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-full border-2 border-brand-navy bg-brand-navy px-6 font-display text-lg font-semibold text-white" data-id="element-324" data-mp="2s75wz27ac4lw">
              Get Connected
              <NewTabHint data-id="element-325" data-mp="29fwmoqpoxbt9" />
            </a>
            <p className="mt-4 font-meta text-[13px] text-brand-navy" data-id="element-326" data-mp="29e1wawvngrhs">
              Thursdays at 7:30 PM • UCF Student Union, 3rd Floor
            </p>
          </nav>
        </div> : null}
    </header>;
}